import * as echarts from 'echarts/core';
import React, { useMemo } from 'react';
import {
  Collapse,
} from 'reactstrap';
import ExpandCollapseIcon from '../../../../components/ExpandCollapseIcon';
import capitalize from '../../../../utils/capitalize';
import getSubCategories from '../../../../utils/get-banking-sub-categories';
import getMonthlyIncome from '../get-monthly-income';
import styles from '../IncomeSourcesCard.module.scss';
import AvgEodBalanceChart from '../IncomeSourcesChart/AvgEodBalanceChart/AvgEodBalanceChartView';
import IncomeTransactionsTable from '../IncomeTransactionsTable';
import renderMonthlyAmount from '../renderMonthlyAmount';
import SubCategory from './SubCategory';

const formatPayFrequency = (payFrequency) => {
  if (!payFrequency) {
    return 'Irregular/Unknown';
  }
  return payFrequency.charAt(0).toUpperCase() + payFrequency.slice(1);
};

const payFrequencyToColor = (payFrequency) => {
  if (!payFrequency) {
    return 'danger';
  }
  if (payFrequency.startsWith('days')) {
    return 'warning';
  }
  return 'success';
};

function StabilityIndicator({ stability }) {
  let color = 'danger';
  if (stability === 'high') {
    color = 'success';
  } else if (stability === 'medium') {
    color = 'warning';
  }
  return (
    <span className={`text-${color}`}>
      {capitalize(stability)}
    </span>
  );
}

const getAvailableCutoffs = (income) => {
  const cutoffSet = new Set(Object.keys(income?.mean_balances_with_offsets ?? {}).map((key) => {
    const [averageGroup] = key.split(/_/).slice(-1);
    return averageGroup;
  }));
  return Array.from(cutoffSet);
};

function CutoffButton({
  days, lastNDays, setLastNDays, availableCutoffs, incomeKey,
}) {
  if (!availableCutoffs.includes(days)) {
    return null;
  }

  const id = `${incomeKey}-cutoff-${days}`;
  return (
    <label
      className={`btn ${styles.cursorPointer} ${days === lastNDays ? 'btn-primary' : 'btn-outline-primary '}`}
      htmlFor={id}
    >
      <input type="radio" name="options" id={id} onClick={() => setLastNDays(days)} />
      {' '}
      {capitalize(days)}
    </label>
  );
}

const isIncomeExtrapolated = (sourceData) => {
  if (!sourceData.monthlyIncome) {
    return false;
  }
  const spanMs = new Date(sourceData.latestDate) - new Date(sourceData.earliestDate);
  const spanDays = spanMs / (1000 * 60 * 60 * 24);
  return spanDays < 30;
};

export default function IncomeSource({
  sourceData, selected, toggleSelected, chartData, transactionCutoffDate,
}) {
  const [isCollapsed, setIsCollapsed] = React.useState(true);
  const [cutoff, setCutoff] = React.useState('all');

  const monthlyNetIncome = getMonthlyIncome(sourceData, false);
  const monthlyGrossIncome = getMonthlyIncome(sourceData, true, 0);
  const incomeExtrapolated = isIncomeExtrapolated(sourceData);

  const availableCutoffs = useMemo(() => getAvailableCutoffs(chartData), [chartData]);

  const alerts = useMemo(() => {
    const baseAlerts = [...sourceData.alerts];
    if (incomeExtrapolated) {
      baseAlerts.push('(*) Income estimated based on less than a month of data');
    }
    return baseAlerts;
  }, [sourceData.alerts, incomeExtrapolated]);

  return (
    <div className={`card my-2 ${selected ? styles.selectedCard : ''}`}>
      <div className={`row card-body py-3 ${styles.clickable}`} onClick={toggleSelected} aria-hidden="true">
        <div className="col-auto d-flex align-items-center">
          <input
            type="checkbox"
            checked={selected}
            className={`${styles.selectIncomeSourceCheckbox}`}
            readOnly
          />
        </div>
        <div className="col text-left">
          <div className="row ml-0">
            <div className={styles.incomeTitle}>{sourceData.description.toUpperCase()}</div>
          </div>
          <div className="row">
            <div className="col-6">
              <div className="row">
                <div className="col-4">Income Category:</div>
                <div className="col">
                  {sourceData.category?.subCategory ? <SubCategory subCategoryList={getSubCategories(sourceData.category)} /> : 'Unknown'}
                </div>
              </div>
              <div className="row">
                <div className="col-4">Stability:</div>
                <div className="col-6">
                  <StabilityIndicator stability={sourceData.incomeSourceStabilityLabel ?? 'Unknown'} />
                </div>
              </div>
              <div className="row">
                <div className="col-4">Frequency:</div>
                <div className="col-6">
                  <span className={`text-${payFrequencyToColor(sourceData.payFrequency)}`}>
                    {formatPayFrequency(sourceData.payFrequency)}
                  </span>
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="row">
                <div className="col-4">Net Income:</div>
                <div className={`col-6 ${styles.incomeTitle}`}>
                  {monthlyNetIncome <= 0 ? <span className="font-weight-bold">Unknown</span> : (
                    renderMonthlyAmount(monthlyNetIncome)
                  )}
                  {incomeExtrapolated && <span className="font-weight-bolder">*</span>}
                </div>
              </div>
              <div className="row">
                <div className="col-4">Est. Gross Income:</div>
                <div className="col-6">
                  {(() => {
                    if (monthlyGrossIncome <= 0) {
                      return monthlyNetIncome <= 0 ? 'Unknown' : 'Unknown (Using Net)';
                    }
                    return renderMonthlyAmount(monthlyGrossIncome);
                  })()}
                  {incomeExtrapolated && <span className="font-weight-bolder">*</span>}
                </div>
              </div>
              {alerts.length > 0 && (
                <div className="row">
                  <div className="col-4">Alerts:</div>
                  <div className="col-6">
                    {alerts.map((alert) => (
                      <div
                        key={alert}
                        className="text-left text-danger"
                      >
                        {alert}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className="col-auto d-flex align-items-center"
          onClick={(e) => {
            e.stopPropagation();
            setIsCollapsed(!isCollapsed);
          }}
          aria-hidden="true"
        >
          <ExpandCollapseIcon
            isCollapsed={isCollapsed}
            toggleIsCollapsed={() => setIsCollapsed(!isCollapsed)}
          />
        </div>
      </div>
      <Collapse isOpen={!isCollapsed} className={`${styles.printable} mx-4 mb-4`}>
        <hr className="mt-0" />
        <div className="row">
          <div className="col">
            <div className="row pb-2">
              <div className="col">
                <div className="row">
                  <div className="col-auto">First Occurrence:</div>
                  <div className="col-auto">
                    {sourceData.earliestDate}
                  </div>
                </div>
                <div className="row">
                  <div className="col-auto">Last Occurrence:</div>
                  <div className="col-auto">
                    {sourceData.latestDate}
                  </div>
                </div>
              </div>
              <div className="col text-right">
                <div className="row">
                  <div className="col-auto">Next payment:</div>
                  <div className="col-auto">
                    <span className={sourceData.nextPaymentDate}>
                      {sourceData.nextPaymentDate ?? 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <IncomeTransactionsTable transactions={sourceData.transactions ?? []} />
          </div>
          <div className="col-6">
            <div className="row d-flex justify-content-end mr-2">
              <div className="btn-group btn-group-toggle justify-content-end" data-toggle="buttons">
                <CutoffButton
                  days="30"
                  lastNDays={cutoff}
                  setLastNDays={setCutoff}
                  availableCutoffs={availableCutoffs}
                  incomeKey={chartData.key}
                />
                <CutoffButton
                  days="90"
                  lastNDays={cutoff}
                  setLastNDays={setCutoff}
                  availableCutoffs={availableCutoffs}
                  incomeKey={chartData.key}
                />
                <CutoffButton
                  days="180"
                  lastNDays={cutoff}
                  setLastNDays={setCutoff}
                  availableCutoffs={availableCutoffs}
                  incomeKey={chartData.key}
                />
                <CutoffButton
                  days="270"
                  lastNDays={cutoff}
                  setLastNDays={setCutoff}
                  availableCutoffs={availableCutoffs}
                  incomeKey={chartData.key}
                />
                <CutoffButton
                  days="all"
                  lastNDays={cutoff}
                  setLastNDays={setCutoff}
                  availableCutoffs={availableCutoffs}
                  incomeKey={chartData.key}
                />
              </div>
            </div>
            <AvgEodBalanceChart
              echarts={echarts}
              income={chartData}
              cutoff={cutoff}
              transactionCutoffDate={transactionCutoffDate}
            />
          </div>
        </div>
      </Collapse>
    </div>
  );
}
